<template>
  <b-modal
    id="outcome-modal"
    :title="'Desfecho — Ocorrência #' + (occurrence ? occurrence.external_id : '')"
    centered
    size="lg"
    @hidden="onHidden"
  >
    <div>
      <div v-if="readOnly" class="alert alert-info mb-3" role="alert">
        <i class="fas fa-eye mr-1"></i> Visualização apenas — sem permissão de edição.
      </div>

      <div v-if="loading" class="text-center py-4">
        <span>Carregando...</span>
      </div>

      <div v-else>
        <!-- Internação -->
        <div class="mb-3">
          <label class="font-weight-bold">Internação</label>
          <div v-if="readOnly" class="form-control-plaintext">{{ formatBool(form.hospitalization) }}</div>
          <div v-else class="d-flex">
            <div class="form-check mr-3">
              <input class="form-check-input" type="radio" :value="true" v-model="form.hospitalization" id="hosp_yes" />
              <label class="form-check-label" for="hosp_yes">Sim</label>
            </div>
            <div class="form-check">
              <input class="form-check-input" type="radio" :value="false" v-model="form.hospitalization" id="hosp_no" />
              <label class="form-check-label" for="hosp_no">Não</label>
            </div>
          </div>
        </div>

        <!-- UTI -->
        <div class="mb-3">
          <label class="font-weight-bold">UTI</label>
          <div v-if="readOnly" class="form-control-plaintext">{{ formatBool(form.uti) }}</div>
          <div v-else class="d-flex">
            <div class="form-check mr-3">
              <input class="form-check-input" type="radio" :value="true" v-model="form.uti" id="uti_yes" />
              <label class="form-check-label" for="uti_yes">Sim</label>
            </div>
            <div class="form-check">
              <input class="form-check-input" type="radio" :value="false" v-model="form.uti" id="uti_no" />
              <label class="form-check-label" for="uti_no">Não</label>
            </div>
          </div>
        </div>

        <!-- Cirurgia -->
        <div class="mb-3">
          <label class="font-weight-bold">Cirurgia</label>
          <div v-if="readOnly" class="form-control-plaintext">{{ formatBool(form.surgery) }}</div>
          <div v-else class="d-flex">
            <div class="form-check mr-3">
              <input class="form-check-input" type="radio" :value="true" v-model="form.surgery" id="surgery_yes" />
              <label class="form-check-label" for="surgery_yes">Sim</label>
            </div>
            <div class="form-check">
              <input class="form-check-input" type="radio" :value="false" v-model="form.surgery" id="surgery_no" />
              <label class="form-check-label" for="surgery_no">Não</label>
            </div>
          </div>
        </div>

        <!-- Diagnóstico CID -->
        <div class="mb-3" style="position: relative;">
          <label class="font-weight-bold">Diagnóstico na Emergência (CID-10)</label>
          <div v-if="readOnly" class="form-control-plaintext">
            <span v-if="form.cid_code"><strong>{{ form.cid_code }}</strong> — {{ form.cid_description }}</span>
            <span v-else class="text-muted">—</span>
          </div>
          <div v-else>
            <div class="input-group">
              <input
                type="text"
                class="form-control"
                v-model="cidSearch"
                placeholder="Digite o código ou descrição..."
                @input="onCidInput"
                @keydown.esc="cidSuggestions = []"
                autocomplete="off"
              />
              <div class="input-group-append">
                <button class="btn btn-outline-secondary btn-sm" type="button" @click="setNaoLocalizado" title="Não localizado">
                  N/L
                </button>
              </div>
            </div>
            <div v-if="cidSuggestions.length" class="list-group" style="position: absolute; z-index: 9999; max-height: 200px; overflow-y: auto; width: 100%;">
              <button
                v-for="cid in cidSuggestions"
                :key="cid.code"
                type="button"
                class="list-group-item list-group-item-action py-1 px-2 text-left"
                @click="selectCid(cid)"
              >
                <strong>{{ cid.code }}</strong> — {{ cid.description }}
              </button>
            </div>
            <small v-if="form.cid_code" class="text-success mt-1 d-block">
              <i class="fas fa-check-circle"></i> {{ form.cid_code }} — {{ form.cid_description }}
            </small>
          </div>
        </div>

        <!-- Status / Desfecho -->
        <div class="mb-3">
          <label class="font-weight-bold">Status / Desfecho</label>
          <div v-if="readOnly" class="form-control-plaintext">{{ form.outcome_status || '—' }}</div>
          <select v-else class="form-control" v-model="form.outcome_status">
            <option value="">Selecione...</option>
            <option v-for="opt in outcomeOptions" :key="opt" :value="opt">{{ opt }}</option>
          </select>
        </div>

        <!-- Observação -->
        <div class="mb-3">
          <label class="font-weight-bold">Observação</label>
          <div v-if="readOnly" class="form-control-plaintext" style="white-space: pre-wrap;">{{ form.observation || '—' }}</div>
          <textarea v-else class="form-control" v-model="form.observation" rows="4" placeholder="Observações adicionais..."></textarea>
        </div>
      </div>
    </div>

    <template #modal-footer>
      <button type="button" class="btn btn-secondary" @click="close">Cancelar</button>
      <button v-if="!readOnly" type="button" class="btn btn-primary" @click="save" :disabled="saving">
        {{ saving ? 'Salvando...' : 'Salvar' }}
      </button>
    </template>
  </b-modal>
</template>

<script>
import { BModal } from 'bootstrap-vue';

export default {
  name: 'OccurrenceOutcomeModal',
  components: { BModal },
  props: {
    occurrence: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      saving: false,
      cidSearch: '',
      cidSuggestions: [],
      cidDebounce: null,
      form: {
        hospitalization: null,
        uti: null,
        surgery: null,
        cid_code: null,
        cid_description: null,
        outcome_status: '',
        observation: '',
      },
      outcomeOptions: [
        'Alta – Melhora do quadro',
        'Alta – Revelia',
        'Alta – Ambulatorial',
        'Não Localizado',
        'Óbito',
        'Permanece Internado(a)',
        'Transferência outra unidade',
      ],
    };
  },
  computed: {
    readOnly() {
      const nivel = this.$store.state.user.access_nivel;
      return ![2, 3, 6].includes(nivel);
    },
  },
  watch: {
    occurrence(val) {
      if (val) {
        this.loadOutcome();
      }
    },
  },
  methods: {
    open() {
      this.$bvModal.show('outcome-modal');
    },
    close() {
      this.$bvModal.hide('outcome-modal');
    },
    onHidden() {
      this.cidSuggestions = [];
      this.cidSearch = '';
    },
    loadOutcome() {
      if (!this.occurrence) return;
      this.loading = true;
      const api = this.$store.state.api + 'occurrence-outcomes/' + this.occurrence.id;
      axios
        .get(api)
        .then((res) => {
          if (res.data) {
            this.form = {
              hospitalization: res.data.hospitalization,
              uti: res.data.uti,
              surgery: res.data.surgery,
              cid_code: res.data.cid_code || null,
              cid_description: res.data.cid_description || null,
              outcome_status: res.data.outcome_status || '',
              observation: res.data.observation || '',
            };
            if (res.data.cid_code) {
              this.cidSearch = res.data.cid_code + ' — ' + res.data.cid_description;
            }
          } else {
            this.resetForm();
          }
        })
        .catch(() => {
          this.resetForm();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    resetForm() {
      this.form = {
        hospitalization: null,
        uti: null,
        surgery: null,
        cid_code: null,
        cid_description: null,
        outcome_status: '',
        observation: '',
      };
      this.cidSearch = '';
    },
    onCidInput() {
      this.form.cid_code = null;
      this.form.cid_description = null;
      clearTimeout(this.cidDebounce);
      if (this.cidSearch.length < 2) {
        this.cidSuggestions = [];
        return;
      }
      this.cidDebounce = setTimeout(() => {
        const api = this.$store.state.api + 'cid/search?q=' + encodeURIComponent(this.cidSearch);
        axios
          .get(api)
          .then((res) => { this.cidSuggestions = res.data || []; })
          .catch(() => { this.cidSuggestions = []; });
      }, 350);
    },
    selectCid(cid) {
      this.form.cid_code = cid.code;
      this.form.cid_description = cid.description;
      this.cidSearch = cid.code + ' — ' + cid.description;
      this.cidSuggestions = [];
    },
    setNaoLocalizado() {
      this.form.cid_code = 'N/L';
      this.form.cid_description = 'Não localizado';
      this.cidSearch = 'N/L — Não localizado';
      this.cidSuggestions = [];
    },
    save() {
      this.saving = true;
      const api = this.$store.state.api + 'occurrence-outcomes/' + this.occurrence.id;
      axios
        .post(api, this.form)
        .then(() => {
          this.$message(null, 'Desfecho salvo com sucesso!', 'success');
          this.close();
          this.$emit('saved');
        })
        .catch((err) => {
          const msg = err.response?.data?.message || 'Erro ao salvar desfecho.';
          this.$message(null, msg, 'error');
        })
        .finally(() => {
          this.saving = false;
        });
    },
    formatBool(val) {
      if (val === null || val === undefined) return '—';
      return val ? 'Sim' : 'Não';
    },
  },
};
</script>
